import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PayPalSettings from '../components/PayPalSettings';
import MumbleChannelManager from '../components/MumbleChannelManager';
import UserManagement from '../components/UserManagement';
import UserMumbleAssignment from '../components/UserMumbleAssignment';
import AclManager from '../components/AclManager';
import api from '../utils/api';
import toast from 'react-hot-toast';

const AdminPage = () => {
  const { user, logout } = useAuth();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'stats');

  // Handle PayPal connection result
  useEffect(() => {
    if (searchParams.get('paypal_connected') === 'true') {
      toast.success('PayPal connected successfully!');
      setActiveTab('settings');
    } else if (searchParams.get('paypal_error')) {
      toast.error('Failed to connect PayPal');
    }
  }, []);
  const [users, setUsers] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [plans, setPlans] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planForm, setPlanForm] = useState({
    name: '',
    description: '',
    price: '',
    billingInterval: 'monthly',
    maxUsers: '',
    features: ''
  });

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      switch (activeTab) {
        case 'stats':
          const statsRes = await api.get('/admin/stats/billing');
          setStats(statsRes.data.stats);
          break;
        case 'users':
          const usersRes = await api.get('/admin/users');
          setUsers(usersRes.data.users);
          break;
        case 'subscriptions':
          const subsRes = await api.get('/admin/subscriptions');
          setSubscriptions(subsRes.data.subscriptions);
          break;
        case 'plans':
          const plansRes = await api.get('/admin/plans');
          setPlans(plansRes.data.plans);
          break;
        default:
          break;
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleUserActive = async (userId, isActive) => {
    try {
      await api.put(`/admin/users/${userId}`, { isActive: !isActive });
      toast.success('User updated successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('User deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const handleTogglePlanActive = async (planId, isActive) => {
    try {
      await api.put(`/admin/plans/${planId}`, { isActive: !isActive });
      toast.success('Plan updated successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to update plan');
    }
  };

  const handleEditPlan = (plan) => {
    setEditingPlan(plan);
    setPlanForm({
      name: plan.name,
      description: plan.description || '',
      price: plan.price,
      billingInterval: plan.billing_interval,
      maxUsers: plan.max_users,
      features: plan.features ? JSON.stringify(plan.features) : ''
    });
    setShowPlanModal(true);
  };

  const handleCreatePlan = () => {
    setEditingPlan(null);
    setPlanForm({
      name: '',
      description: '',
      price: '',
      billingInterval: 'monthly',
      maxUsers: '',
      features: ''
    });
    setShowPlanModal(true);
  };

  const handleSavePlan = async (e) => {
    e.preventDefault();
    try {
      const data = {
        name: planForm.name,
        description: planForm.description,
        price: parseFloat(planForm.price),
        billingInterval: planForm.billingInterval,
        maxUsers: parseInt(planForm.maxUsers),
        features: planForm.features ? JSON.parse(planForm.features) : {}
      };

      if (editingPlan) {
        await api.put(`/admin/plans/${editingPlan.id}`, data);
        toast.success('Plan updated successfully');
      } else {
        await api.post('/admin/plans', data);
        toast.success('Plan created successfully');
      }

      setShowPlanModal(false);
      fetchData();
    } catch (error) {
      toast.error(editingPlan ? 'Failed to update plan' : 'Failed to create plan');
    }
  };

  return (
    <div>
      <nav className="navbar">
        <Link to="/" className="navbar-brand">
          🎙️ Steinmetz Mumble
        </Link>
        <div className="navbar-menu">
          <span style={{ color: 'white' }}>{user?.fullName} (Admin)</span>
          <Link to="/dashboard" className="btn btn-secondary">
            Dashboard
          </Link>
          <button onClick={logout} className="btn btn-secondary">
            Logout
          </button>
        </div>
      </nav>

      <div className="container">
        <h1 style={{ color: 'white', marginBottom: '2rem' }}>Admin Panel</h1>

        <div className="tabs">
          <button
            className={`tab ${activeTab === 'stats' ? 'active' : ''}`}
            onClick={() => setActiveTab('stats')}
          >
            Statistics
          </button>
          <button
            className={`tab ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            Users
          </button>
          <button
            className={`tab ${activeTab === 'subscriptions' ? 'active' : ''}`}
            onClick={() => setActiveTab('subscriptions')}
          >
            Subscriptions
          </button>
          <button
            className={`tab ${activeTab === 'plans' ? 'active' : ''}`}
            onClick={() => setActiveTab('plans')}
          >
            Plans
          </button>
          <button
            className={`tab ${activeTab === 'mumble' ? 'active' : ''}`}
            onClick={() => setActiveTab('mumble')}
          >
            Mumble Channels
          </button>
          <button
            className={`tab ${activeTab === 'mumble-users' ? 'active' : ''}`}
            onClick={() => setActiveTab('mumble-users')}
          >
            Mumble User Assignment
          </button>
          <button
            className={`tab ${activeTab === 'acl' ? 'active' : ''}`}
            onClick={() => setActiveTab('acl')}
          >
            Permissions
          </button>
          <button
            className={`tab ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            Settings
          </button>
        </div>

        {loading ? (
          <div className="card">
            <p style={{ textAlign: 'center' }}>Loading...</p>
          </div>
        ) : (
          <>
            {activeTab === 'stats' && stats && (
              <div>
                <div className="dashboard-grid">
                  <div className="stat-card">
                    <h3>Total Revenue</h3>
                    <div className="value">${stats.totalRevenue.toFixed(2)}</div>
                  </div>
                  <div className="stat-card">
                    <h3>Monthly Revenue</h3>
                    <div className="value">${stats.monthlyRevenue.toFixed(2)}</div>
                  </div>
                  <div className="stat-card">
                    <h3>Active Subscriptions</h3>
                    <div className="value">{stats.activeSubscriptions}</div>
                  </div>
                  <div className="stat-card">
                    <h3>Total Users</h3>
                    <div className="value">{stats.totalUsers}</div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'users' && (
              <UserManagement />
            )}

            {activeTab === 'users-old' && (
              <div className="card">
                <h2 style={{ marginBottom: '1rem' }}>User Management</h2>
                <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Plan</th>
                      <th>Status</th>
                      <th>Joined</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id}>
                        <td>{u.full_name}</td>
                        <td>{u.email}</td>
                        <td>{u.plan_name || 'None'}</td>
                        <td>
                          <span className={`badge badge-${u.is_active ? 'success' : 'danger'}`}>
                            {u.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>{new Date(u.created_at).toLocaleDateString()}</td>
                        <td>
                          <button
                            className="btn btn-secondary"
                            style={{ marginRight: '0.5rem', padding: '0.3rem 0.6rem' }}
                            onClick={() => handleToggleUserActive(u.id, u.is_active)}
                          >
                            {u.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                          {!u.is_admin && (
                            <button
                              className="btn btn-danger"
                              style={{ padding: '0.3rem 0.6rem' }}
                              onClick={() => handleDeleteUser(u.id)}
                            >
                              Delete
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            )}

            {activeTab === 'subscriptions' && (
              <div className="card">
                <h2 style={{ marginBottom: '1rem' }}>Subscription Management</h2>
                <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Plan</th>
                      <th>Status</th>
                      <th>Price</th>
                      <th>Period End</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscriptions.map((sub) => (
                      <tr key={sub.id}>
                        <td>
                          {sub.full_name}
                          <br />
                          <small style={{ color: '#666' }}>{sub.email}</small>
                        </td>
                        <td>{sub.plan_name}</td>
                        <td>
                          <span
                            className={`badge badge-${
                              sub.status === 'active'
                                ? 'success'
                                : sub.status === 'cancelled'
                                ? 'danger'
                                : 'warning'
                            }`}
                          >
                            {sub.status}
                          </span>
                        </td>
                        <td>${sub.price}</td>
                        <td>{new Date(sub.current_period_end).toLocaleDateString()}</td>
                        <td>{new Date(sub.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            )}

            {activeTab === 'mumble' && (
              <MumbleChannelManager />
            )}

            {activeTab === 'mumble-users' && (
              <UserMumbleAssignment />
            )}

            {activeTab === 'acl' && (
              <AclManager />
            )}

            {activeTab === 'settings' && (
              <PayPalSettings />
            )}

            {activeTab === 'plans' && (
              <div className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h2 style={{ margin: 0 }}>Plan Management</h2>
                  <button
                    className="btn btn-primary"
                    onClick={handleCreatePlan}
                  >
                    Create New Plan
                  </button>
                </div>
                <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Price</th>
                      <th>Interval</th>
                      <th>Max Users</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plans.map((plan) => (
                      <tr key={plan.id}>
                        <td>{plan.name}</td>
                        <td>${plan.price}</td>
                        <td>{plan.billing_interval}</td>
                        <td>{plan.max_users}</td>
                        <td>
                          <span className={`badge badge-${plan.is_active ? 'success' : 'danger'}`}>
                            {plan.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>
                          <button
                            className="btn btn-primary"
                            style={{ padding: '0.3rem 0.6rem', marginRight: '0.5rem', fontSize: '0.85rem' }}
                            onClick={() => handleEditPlan(plan)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-secondary"
                            style={{ padding: '0.3rem 0.6rem', fontSize: '0.85rem' }}
                            onClick={() => handleTogglePlanActive(plan.id, plan.is_active)}
                          >
                            {plan.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Plan Edit/Create Modal */}
      {showPlanModal && (
        <div className="modal-overlay" onClick={() => setShowPlanModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{editingPlan ? 'Edit Plan' : 'Create New Plan'}</h2>
            <form onSubmit={handleSavePlan}>
              <div className="form-group">
                <label>Plan Name *</label>
                <input
                  type="text"
                  className="form-input"
                  value={planForm.name}
                  onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  className="form-input"
                  value={planForm.description}
                  onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })}
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label>Price (USD) *</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-input"
                  value={planForm.price}
                  onChange={(e) => setPlanForm({ ...planForm, price: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Billing Interval *</label>
                <select
                  className="form-input"
                  value={planForm.billingInterval}
                  onChange={(e) => setPlanForm({ ...planForm, billingInterval: e.target.value })}
                  required
                >
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>

              <div className="form-group">
                <label>Max Users *</label>
                <input
                  type="number"
                  className="form-input"
                  value={planForm.maxUsers}
                  onChange={(e) => setPlanForm({ ...planForm, maxUsers: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Features (JSON)</label>
                <textarea
                  className="form-input"
                  value={planForm.features}
                  onChange={(e) => setPlanForm({ ...planForm, features: e.target.value })}
                  rows="4"
                  placeholder='{"key": "value"}'
                />
                <small style={{ color: '#666', fontSize: '0.85rem' }}>
                  Enter features as JSON object (optional)
                </small>
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>
                  {editingPlan ? 'Update Plan' : 'Create Plan'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  style={{ flex: 1 }}
                  onClick={() => setShowPlanModal(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
